import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCheck } from "lucide-react";
import NotificationItem from "./NotificationItem";
import useNotifications from "../../hooks/useNotifications";
import { getNotificationPath } from "../../utils/notificationRoutes";

/**
 * Dropdown panel listing recent notifications.
 */
export default function NotificationPanel({
  onClose,
  onViewAll,
  maxItems = 15,
}) {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    removeNotification,
    isStaff,
  } = useNotifications();

  const items = (notifications || []).slice(0, maxItems);
  const hasUnread = items.some((n) => n.isRead === false);

  const handleItemClick = async (n) => {
    if (!n) return;
    if (!n.isRead) {
      await markAsRead(n._id);
    }
    const path = getNotificationPath(n, isStaff);
    onClose?.();
    if (path) {
      navigate(path);
    }
  };

  const handleMarkAll = async () => {
    await markAllAsRead();
  };

  return (
    <div className="absolute right-0 z-50 mt-2 w-72 sm:w-80 max-w-[90vw] overflow-hidden rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-popover dark:shadow-black/40">
      <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 px-4 py-2.5 sm:py-3">
        <span className="font-display text-title-sm text-neutral-900 dark:text-neutral-100">
          Notifications
        </span>
        <button
          type="button"
          onClick={handleMarkAll}
          disabled={!hasUnread}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] sm:text-label-sm font-medium transition-colors ${
            hasUnread
              ? "bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-500/15 dark:text-primary-400 dark:hover:bg-primary-500/25"
              : "bg-neutral-100 text-neutral-400 cursor-not-allowed dark:bg-neutral-800 dark:text-neutral-600"
          }`}
        >
          <CheckCheck size={12} />
          <span className="hidden sm:inline">Mark all read</span>
        </button>
      </div>

      <ul className="max-h-72 sm:max-h-80 overflow-y-auto">
        {loading && items.length === 0 && (
          <li className="px-4 py-6 text-center text-xs text-neutral-400 dark:text-neutral-500">
            Loading…
          </li>
        )}
        {!loading && items.length === 0 && (
          <li className="px-4 py-6 text-center text-xs text-neutral-400 dark:text-neutral-500">
            No notifications yet
          </li>
        )}
        {items.map((n) => (
          <NotificationItem
            key={n._id}
            notification={n}
            onClick={handleItemClick}
            onMarkAsRead={(item) => markAsRead(item._id)}
            onDelete={(item) => removeNotification(item._id)}
            compact
          />
        ))}
      </ul>

      <button
        type="button"
        onClick={() => {
          onClose?.();
          onViewAll?.();
        }}
        className="block w-full border-t border-neutral-100 dark:border-neutral-800 px-4 py-2 text-center text-xs sm:text-label-lg font-medium text-primary-600 dark:text-primary-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
      >
        View all notifications
      </button>
    </div>
  );
}